<template>
    <div
        ref="rootEl"
        :class="['a-small-card-flip', className]"
        @click="onCardClick"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
        @focusin="onFocusIn"
        @focusout="onFocusOut"
        @keydown.esc="onEscape"
    >
        <div class="cards">
            <div class="card front" :inert="isFlipped" :aria-hidden="isFlipped">
                <a-small-card
                    v-bind="frontContent"
                    :theme="theme"
                    :to="null"
                />
                <div v-if="frontTrigger && isFlippable" class="trigger-row">
                    <button
                        ref="frontTriggerEl"
                        type="button"
                        class="trigger"
                        :aria-label="frontTrigger.label || `${frontTrigger.text}: ${frontTitle}`"
                        :aria-expanded="isFlipped"
                        :aria-controls="backId"
                        @click.stop="flipTo(true, true)"
                    >
                        <span v-if="frontTrigger.text">{{ frontTrigger.text }}</span>
                        <a-glyph v-if="frontTrigger.glyph" :name="frontTrigger.glyph" :aria-hidden="true" />
                    </button>
                </div>
            </div>
            <div
                v-if="isFlippable"
                :id="backId"
                class="card back"
                :inert="!isFlipped"
                :aria-hidden="!isFlipped"
            >
                <button
                    v-if="hasCloseIcon"
                    ref="closeEl"
                    type="button"
                    class="close"
                    :aria-label="back.closeLabel || `Close details: ${frontTitle}`"
                    @click.stop="flipTo(false, true)"
                >
                    <a-glyph name="i-revoke--24" :aria-hidden="true" />
                </button>
                <a-small-card
                    v-bind="backContent"
                    :to="null"
                    :theme="theme"
                />
                <div v-if="backTrigger" class="trigger-row">
                    <button
                        ref="backTriggerEl"
                        type="button"
                        class="trigger trigger-back"
                        :aria-label="backTrigger.label || `${backTrigger.text}: ${frontTitle}`"
                        @click.stop="flipTo(false, true)"
                    >
                        <a-glyph v-if="backTrigger.glyph" :name="backTrigger.glyph" :aria-hidden="true" />
                        <span v-if="backTrigger.text">{{ backTrigger.text }}</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, nextTick, ref, useId } from 'vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import ASmallCard from '@core/components/small-card/small-card.vue';
import common from '@utils/common';

defineOptions({
    name: 'ASmallCardFlip',
});

const props = defineProps({
    front: {
        type: Object,
        default: null,
    },
    back: {
        type: Object,
        default: null,
    },
    flipOn: {
        type: [String, Array],
        default: null,
        validator: (prop) => [].concat(prop).filter(Boolean).every((v) => ['clickCard', 'hover'].includes(v)),
    },
    flipDirection: {
        type: String,
        default: null,
        validator: (prop) => !prop || ['right', 'left', 'up', 'down'].includes(prop),
    },
    peekOnHover: {
        type: Boolean,
        default: true,
    },
    titleTag: {
        type: String,
        default: 'p',
    },
    theme: {
        type: String,
        validator: (prop) => ['light', 'dark'].includes(prop),
        default: 'light',
    },
});

function toCardContent(source) {
    const content = { ...(source || {}) };
    delete content.trigger;
    delete content.hasCloseIcon;
    delete content.closeLabel;
    content.titleTag = content.titleTag || props.titleTag;
    if (Array.isArray(content.labels)) {
        content.labels = content.labels.map((label) => ({ size: 'm', ...label }));
    }
    return content;
}

const frontContent = computed(() => toCardContent(props.front));
const backContent = computed(() => toCardContent(props.back));

const frontTrigger = computed(() => props.front?.trigger || null);
const backTrigger = computed(() => props.back?.trigger || null);
const hasCloseIcon = computed(() => Boolean(props.back) && props.back.hasCloseIcon !== false);

const rootEl = ref(null);
const frontTriggerEl = ref(null);
const backTriggerEl = ref(null);
const closeEl = ref(null);
const backId = `small-card-back-${useId()}`;
const frontTitle = computed(() => common.altText(props.front?.title));
let movingFocus = false;
const isFlipped = ref(false);
const isFlippable = computed(() => Boolean(props.back));

const flipModes = computed(() => [].concat(props.flipOn).filter(Boolean));
const canContainerClick = computed(() => flipModes.value.includes('clickCard'));
const canHoverFlip = computed(() => flipModes.value.includes('hover'));

const isAnimated = computed(() => isFlippable.value && Boolean(props.flipDirection));

const className = computed(() => ({
    flippable: isFlippable.value,
    flipped: isFlippable.value && isFlipped.value,
    clickable: isFlippable.value && canContainerClick.value,
    animated: isAnimated.value,
    'hover-peek': props.peekOnHover,
    [`flip-${props.flipDirection}`]: isAnimated.value,
    'is-dark': props.theme === 'dark',
}));

const INTERACTIVE_SELECTOR = 'a, button, input, select, textarea, label, [role="button"], .a-button, .a-link';

async function flipTo(state, moveFocus = false) {
    if (!isFlippable.value) return;
    isFlipped.value = state;
    if (!moveFocus) return;
    await nextTick();
    if (isFlipped.value !== state) return;
    const target = state ? (backTriggerEl.value || closeEl.value) : frontTriggerEl.value;
    movingFocus = true;
    target?.focus({ preventScroll: true });
    movingFocus = false;
}

function onEscape(event) {
    if (!isFlipped.value) return;
    event.stopPropagation();
    flipTo(false, true);
}

function onCardClick(event) {
    if (!canContainerClick.value) return;
    if (event.target.closest(INTERACTIVE_SELECTOR)) return;
    flipTo(!isFlipped.value);
}

function onMouseEnter() {
    if (canHoverFlip.value) flipTo(true);
}

function onMouseLeave() {
    if (!canHoverFlip.value) return;
    // keep the card flipped while the keyboard is still inside it
    if (rootEl.value?.contains(document.activeElement)) return;
    flipTo(false);
}

function onFocusIn() {
    if (canHoverFlip.value && !movingFocus) flipTo(true);
}

function onFocusOut(event) {
    if (!canHoverFlip.value) return;
    if (event.currentTarget.contains(event.relatedTarget)) return;
    flipTo(false);
}
</script>

<style lang="pcss" scoped>
.a-small-card-flip {
    width: 100%;
    height: 100%;
}

.cards {
    display: grid;
    width: 100%;
    height: 100%;
}

.card {
    grid-area: 1 / 1;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px;
    background-color: var(--av-inversed-primary);
    border: 1px solid var(--av-brand-secondary-light);
    border-radius: 16px;
    overflow: hidden;
    transition: border-color 0.15s ease-out, box-shadow 0.15s ease-out;

    :deep(.a-small-card) {
        flex: 1 1 auto;
        height: auto;
        padding: 0;
        border: none;
        border-radius: 0;
        background-color: transparent;
        box-shadow: none;
        overflow: visible;
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;
        transform: translateZ(0);
        -webkit-transform: translateZ(0);

        .a-picture-logo {
            max-width: 248px;
        }
    }
}

.back {
    border-color: var(--av-brand-primary);
    box-shadow: 0 8px 24px rgba(0, 32, 77, 0.16);

    :deep(.a-small-card) {
        gap: 0;

        .title {
            @mixin descriptor-accent;
            margin-bottom: 16px;
            padding-bottom: 16px;
            padding-inline-end: 32px;
            color: var(--av-fixed-light);
            border-bottom: 1px solid var(--av-fixed-info-accent);
        }

        .list {
            gap: 0;
            list-style: none;
            padding-inline-start: 0;
            color: var(--av-nav-primary);
        }

        .text {
            @mixin paragraph;
            color: var(--av-nav-primary);
        }

        .list-item {
            @mixin paragraph;
            display: block;
            position: relative;
            padding-inline-start: 16px;

            &::before {
                content: '';
                position: absolute;
                inset-inline-start: 0;
                top: 0.7em;
                width: 3px;
                height: 3px;
                background-color: currentColor;
            }
        }

        .links {
            margin-top: 24px;
        }

        .buttons {
            padding-top: 0;
            margin: 40px 0;
        }
    }
}

.front {
    :deep(.a-small-card) {
        gap: 24px;

        .labels,
        .labels-item {
            height: 24px;
        }

        .link-name {
            @mixin caption;
            color: var(--av-nav-primary);
        }

        .link-size {
            @mixin caption;
            color: #8c95ad;
        }

        .labels + .title {
            margin-top: -16px;
        }

        .title {
            @mixin title-accent;
            padding-inline-end: 24px;
            color: var(--av-nav-primary);
        }

        .text {
            @mixin lead;
            padding-inline-end: 24px;
            color: var(--av-nav-primary);
        }
    }
}

.trigger-row {
    padding-top: 24px;
    border-top: 1px solid var(--av-brand-secondary-accent);
}

.trigger {
    gap: 8px;
    border: none;
    display: inline-flex;
    cursor: pointer;
    background: none;
    align-items: center;
    color: var(--av-brand-primary);

    span {
        @mixin paragraph-accent;
        color: var(--av-link-primary);
    }

    .a-glyph {
        fill: var(--av-link-primary);
        height: 16px;
    }

    &:hover span {
        color: var(--av-link-primary-hover);
    }

    &:hover .a-glyph {
        fill: var(--av-link-primary-hover);
    }
}

.close {
    position: absolute;
    top: 20px;
    inset-inline-end: 20px;
    display: flex;
    padding: 4px;
    border: none;
    background: none;
    cursor: pointer;
    color: var(--av-fixed-light);
    z-index: 1;

    .a-glyph {
        fill: var(--av-fixed-light);
    }

    &:hover {
        color: var(--av-fixed-danger);

        .a-glyph {
            fill: var(--av-fixed-danger);
        }
    }
}

.clickable {
    cursor: pointer;

    /* dragging across the face that flips on click should not select its text */
    .front {
        user-select: none;
    }
}

.flip-right {
    --flip-rotate: rotateY(180deg);
    --flip-peek: rotateY(14deg);
}

.flip-left {
    --flip-rotate: rotateY(-180deg);
    --flip-peek: rotateY(-14deg);
}

.flip-up {
    --flip-rotate: rotateX(180deg);
    --flip-peek: rotateX(14deg);
}

.flip-down {
    --flip-rotate: rotateX(-180deg);
    --flip-peek: rotateX(-14deg);
}

.flippable {
    position: relative;
    perspective: 1600px;

    .back {
        pointer-events: none;
    }

    &.flipped {
        .front {
            pointer-events: none;
        }

        .back {
            pointer-events: auto;
        }
    }
}

.flippable:not(.animated) {
    .back {
        display: none;
    }

    &.flipped {
        .front {
            display: none;
        }

        .back {
            display: flex;
        }
    }
}

.flippable.animated {
    --flip-duration: 0.8s;
    --flip-easing: cubic-bezier(0.4, 0.2, 0.2, 1);

    .cards {
        transform: translateZ(0);
        transform-style: preserve-3d;
        -webkit-transform-style: preserve-3d;
        transition: transform 0.6s cubic-bezier(0.4, 0.2, 0.2, 1);
    }

    .card {
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;
    }

    .front,
    .back {
        transition: transform var(--flip-duration) var(--flip-easing);
    }

    .front {
        z-index: 1;
        transform: translateZ(0);
    }

    .back {
        transform: var(--flip-rotate, rotateY(180deg)) translateZ(0);
    }

    &.flipped .cards {
        transform: none;
    }

    &.flipped {
        .front {
            z-index: 0;
            transform: var(--flip-rotate, rotateY(180deg)) translateZ(0);
        }

        .back {
            z-index: 1;
            transform: translateZ(0);
        }
    }
}

@media (hover: hover) {
    .flippable.animated.hover-peek:not(.flipped) .cards:has(.front .trigger:hover),
    .flippable.animated.hover-peek.clickable:not(.flipped):hover .cards {
        transform: var(--flip-peek, rotateY(-14deg));

        .front {
            box-shadow: 0 8px 24px rgba(0, 32, 77, 0.12);
        }
    }
}

@media (prefers-reduced-motion: reduce) {
    .flippable.animated .cards,
    .flippable.animated .front,
    .flippable.animated .back {
        transition: none;
    }

    .flippable.animated.hover-peek:not(.flipped) .cards:has(.front .trigger:hover),
    .flippable.animated.hover-peek.clickable:not(.flipped):hover .cards {
        transform: none;
    }
}

.is-dark {
    .card {
        border-color: var(--av-brand-secondary-light);
        background-color: var(--av-brand-secondary-accent);
    }

    :deep(.a-small-card) {
        .title {
            color: var(--av-link-inversed);
        }

        .text,
        .list {
            color: var(--av-inversed-primary);
        }

        .links-item {
            border-color: var(--av-link-inversed-light);
        }

        .link-item {
            .link-name {
                color: var(--av-inversed-primary);
            }

            .link-size {
                color: var(--av-link-inversed-light);
            }
        }

        .link-icon,
        .link-download {
            fill: var(--av-link-inversed);
        }
    }

    .trigger-row {
        border-color: var(--av-link-inversed-light);
    }

    .trigger {
        span {
            color: var(--av-link-inversed);
        }

        .a-glyph {
            fill: var(--av-link-inversed);
        }
    }

    .close {
        .a-glyph {
            fill: var(--av-link-inversed);
        }
    }
}
</style>
